import React, { FC, useContext, useEffect, useState } from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, DraggableIcon, Input, Tooltip } from '@smartshell/ui-kit';
import cn from 'classnames';
import { Controller, useForm } from 'react-hook-form';

import { LocalizationContext } from 'localization';

import s from '../../GroupModal.module.scss';
import { IGroupForm } from '../../interfaces';
import { IGroupItemProps } from './interfaces';

const GroupItem: FC<IGroupItemProps> = ({
  group,
  disabled,
  noDrag,
  onChange,
  updatedTitle,
  onDelete,
  onValidationError,
}) => {
  const { t } = useContext(LocalizationContext);

  const [localError, setLocalError] = useState<string | null>(null);

  const { control, handleSubmit, watch, reset, trigger } = useForm<IGroupForm>({
    mode: 'all',
    defaultValues: {
      title: group.title,
    },
  });

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: group.id,
      data: {
        type: 'GroupItem',
      },
    });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const title = watch('title');

  useEffect(() => {
    reset({ title: updatedTitle ?? group.title });
  }, [updatedTitle, group.title]);

  useEffect(() => {
    const validate = async () => {
      if (title !== group.title) {
        const isValid = await trigger('title');
        if (!isValid) {
          let error = null;
          if (title.length < 2) {
            error = t('errors.minLength', 2);
          } else if (title.length > 50) {
            error = t('errors.maxLength', 50);
          } else if (!title) {
            error = t('errors.required');
          }
          onValidationError(group.id, error);
        } else {
          onValidationError(group.id, null);
          onChange?.(group.id, title);
        }
      }
    };

    validate();
  }, [title, onChange]);

  useEffect(() => {
    const validate = async () => {
      if (title !== group.title) {
        const isValid = await trigger('title');
        if (!isValid) {
          if (title.length < 2) setLocalError(t('errors.minLength', 2));
          else if (title.length > 50) setLocalError(t('errors.maxLength', 50));
          else if (!title) setLocalError(t('errors.required'));
        } else {
          setLocalError(null);
        }
      }
    };
    validate();
  }, [title, localError]);

  const isDeleteDisabled = disabled || title !== group.title;

  return (
    <div
      className={cn(s.group, noDrag && s.noDrag)}
      ref={setNodeRef}
      style={style}
    >
      {!noDrag && (
        <div {...listeners} {...attributes}>
          <DraggableIcon size="m" />
        </div>
      )}
      <form
        onSubmit={handleSubmit(({ title }) => {
          if (title === group.title) return;
        })}
        className={s.form}
      >
        <Controller
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              variant="primary"
              {...field}
              error={localError || error?.message}
              title={t('title.placeholder')}
              rightContent={
                isDeleteDisabled ? (
                  <Tooltip
                    contentClassName={s.tooltip}
                    content={
                      disabled
                        ? t('group.modal.bucket.tooltip')
                        : t('group.modal.bucket.tooltip_2')
                    }
                    position={'bottom-right'}
                  >
                    <Button
                      variant="ghost"
                      onlyIcon
                      iconName="BucketIcon"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                      className={cn(s.bucket, isDeleteDisabled && s.disabled)}
                      disabled={isDeleteDisabled}
                    />
                  </Tooltip>
                ) : (
                  <Button
                    variant="ghost"
                    onlyIcon
                    iconName="BucketIcon"
                    onClick={(event) => {
                      event.stopPropagation();
                      onDelete?.(group);
                    }}
                    className={cn(s.bucket, isDeleteDisabled && s.disabled)}
                    disabled={isDeleteDisabled}
                  />
                )
              }
              className={s.input}
            />
          )}
          name="title"
          rules={{
            required: t('errors.required'),
            minLength: {
              value: 2,
              message: t('errors.minLength', 2),
            },
            maxLength: {
              value: 50,
              message: t('errors.maxLength', 50),
            },
          }}
        />
      </form>
    </div>
  );
};

export default GroupItem;
